import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getMealMenus, createMealMenu, updateMealMenu, deleteMealMenu,
} from '../../../services/mealMenuService';
import { getMeals } from '../../../services/mealService';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';

const useMealMenuApi = (queryParams) => {
  const { t, i18n } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [meals, setMeals] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const params = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = params;
    setFetching(true);
    getMealMenus(params)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  useEffect(() => {
    getMeals({ pageSize: 100 }).then((res) => {
      const result = res.data?.data;
      setMeals(Array.isArray(result) ? result : (result?.data || []));
    });
  }, []);

  // Items round-trip between JSON array (backend) and comma-separated string
  // (form input). Consumer drawer shows items as a text field; save parses.
  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      const payload = {
        ...formData,
        items: parseItems(formData.items),
        calories: numberOrNull(formData.calories),
        proteinGrams: numberOrNull(formData.proteinGrams),
        fatGrams: numberOrNull(formData.fatGrams),
        carbsGrams: numberOrNull(formData.carbsGrams),
      };
      await createMealMenu(payload);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      const payload = {
        ...formData,
        items: parseItems(formData.items),
        calories: numberOrNull(formData.calories),
        proteinGrams: numberOrNull(formData.proteinGrams),
        fatGrams: numberOrNull(formData.fatGrams),
        carbsGrams: numberOrNull(formData.carbsGrams),
      };
      await updateMealMenu(id, payload);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      await Promise.all(ids.map((id) => deleteMealMenu(id)));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const mealOptions = useMemo(() => meals.map((m) => ({ id: m.id, name: m.name })), [meals]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    dateFilterColumn('menuDate', t('canteen.menuDate'), i18n.language),
    {
      name: 'mealName', label: t('canteen.meal'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    {
      name: 'items', label: t('canteen.items'),
      options: {
        filter: true, sort: false,
        filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (v) => Array.isArray(v) ? v.join(', ') : (v || '-'),
      },
    },
    numericFilterColumn('calories', t('canteen.calories'), { decimals: 0 }),
    numericFilterColumn('proteinGrams', t('canteen.proteinGrams'), { decimals: 1 }),
    numericFilterColumn('fatGrams', t('canteen.fatGrams'), { decimals: 1 }),
    numericFilterColumn('carbsGrams', t('canteen.carbsGrams'), { decimals: 1 }),
    {
      name: 'notes', label: t('canteen.notes'),
      options: {
        filter: true, sort: false,
        filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (v) => v || '-',
      },
    },
  ], [t, i18n.language]);

  const objectSchema = useMemo(() => [
    { name: 'menuDate', label: t('canteen.menuDate'), type: 'date', grid: 6, required: true },
    {
      name: 'mealId', label: t('canteen.meal'), type: 'select', grid: 6, required: true,
      options: mealOptions, idKey: 'id', labelKey: 'name',
    },
    {
      name: 'items', label: t('canteen.items'), type: 'textarea', grid: 12,
      placeholder: t('canteen.itemsHint'),
    },
    { name: 'calories', label: t('canteen.calories'), type: 'number', grid: 3 },
    { name: 'proteinGrams', label: t('canteen.proteinGrams'), type: 'number', grid: 3 },
    { name: 'fatGrams', label: t('canteen.fatGrams'), type: 'number', grid: 3 },
    { name: 'carbsGrams', label: t('canteen.carbsGrams'), type: 'number', grid: 3 },
    { name: 'notes', label: t('canteen.notes'), type: 'textarea', grid: 12 },
  ], [t, mealOptions]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete, refresh };
};

function parseItems(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return String(raw)
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function numberOrNull(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export default useMealMenuApi;
